import React, { type ChangeEvent, useRef, useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { ReactComponent as ChevronLeft } from '@pkg/assets/icons/v2/chevron-left.svg';
import { ButtonBack, ButtonIcon } from '@pkg/components/Sections/Profile/Buttons';
import { ReactComponent as SearchIcon } from '@pkg/components/Sections/Profile/icons/search.svg';

import { Search, type SearchProps } from './components';

import {
    HeaderWrapper,
    Interior,
    Main,
    MainChild,
    Prefix,
    PrefixAction,
    SearchBackground,
    SearchBackgroundCircle,
    Suffix,
    SuffixAction,
    Title,
} from './styles';

// TODO: Some of these props can maybe be consolidated / removed
// once we have further optimized this component.
export interface SheetHeaderProps {
    title?: string;
    searchValue?: SearchProps['value'];
    searchPlaceholder?: SearchProps['placeholder'];
    showSearch?: boolean;
    onSearchInput?(event: ChangeEvent<HTMLInputElement>): void;
    onSearchClear?(): void;
    onClose(): void;
    // TODO: Decide if we really want to support left-aligned titles.
    largeTitle?: boolean;
}

export function Header({
    title,
    searchValue,
    searchPlaceholder,
    showSearch = false,
    onSearchInput,
    onSearchClear,
    onClose,
    largeTitle = false,
}: SheetHeaderProps) {
    const [openSearch, setOpenSearch] = useState(false);
    const searchRef = useRef<HTMLInputElement>(null);

    const handleInputFocus = () => {
        if (searchRef.current) {
            searchRef.current.focus();
        }
    };

    const handleCloseSearch = () => {
        setOpenSearch(false);
        onSearchClear && onSearchClear();
    };

    const searchBackgroundMarkup = openSearch ? (
        <SearchBackground>
            <SearchBackgroundCircle />
        </SearchBackground>
    ) : null;

    // TODO: Improve this once we have cleaned up the <Sheet /> component(s).
    const canHaveLargeTitle = largeTitle && !showSearch;

    const titleMarkup =
        title && !openSearch ? (
            <MainChild key="Sheet-Header-Title">
                <Title $largeTitle={canHaveLargeTitle}>{title}</Title>
            </MainChild>
        ) : null;

    const searchMarkup =
        openSearch && onSearchInput ? (
            <MainChild key="Sheet-Header-Search" onAnimationComplete={handleInputFocus}>
                <Search
                    ref={searchRef}
                    value={searchValue}
                    placeholder={searchPlaceholder}
                    onClear={handleCloseSearch}
                    onChange={onSearchInput}
                />
            </MainChild>
        ) : null;

    // TODO: Requires aria-label
    const searchOpenActionMarkup = openSearch ? null : (
        <PrefixAction key="Sheet-Header-SearchAction">
            <ButtonIcon color="secondary" variant="solid" onClick={() => setOpenSearch(true)}>
                <SearchIcon />
            </ButtonIcon>
        </PrefixAction>
    );

    // TODO: Requires aria-label
    const searchCloseActionMarkup = openSearch ? (
        <PrefixAction key="Sheet-Header-CloseAction">
            <ButtonIcon color="secondary" variant="solid" onClick={handleCloseSearch}>
                <ChevronLeft />
            </ButtonIcon>
        </PrefixAction>
    ) : null;

    return (
        <HeaderWrapper>
            <AnimatePresence>{showSearch && searchBackgroundMarkup}</AnimatePresence>

            <Interior>
                <Prefix>
                    <AnimatePresence initial={false} mode="wait">
                        {showSearch && searchOpenActionMarkup}
                        {showSearch && searchCloseActionMarkup}
                    </AnimatePresence>
                </Prefix>

                <Main $largeTitle={canHaveLargeTitle} $searchMode={showSearch}>
                    <AnimatePresence initial={false} mode="wait">
                        {titleMarkup}
                        {showSearch && searchMarkup}
                    </AnimatePresence>
                </Main>

                <Suffix $disabled={openSearch}>
                    <SuffixAction>
                        {/* TODO: Requires aria-label */}
                        <ButtonBack color="secondary" variant="solid" onClick={onClose} />
                    </SuffixAction>
                </Suffix>
            </Interior>
        </HeaderWrapper>
    );
}
